// src/components/modals/guides/integrationXeroSetup.js

import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FiArrowLeft,
    FiArrowRight,
    FiCheckCircle,
    FiCopy,
    FiLink,
    FiRefreshCw,
    FiX,
    FiSettings,
    FiFileText
} from 'react-icons/fi';

const steps = [
    {
        title: 'Xero Integration Is Here',
        subtitle: 'Track, view, and sync Xero invoices directly inside Notify.',
        icon: FiCheckCircle,
        content: (
            <>
                <img
                    className="w-full max-w-full rounded-xl object-cover"
                    src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/guides/xeroIntegrationSetup/XeroUpdateBanner.png"
                    alt=""
                />
                <p>
                    Notify can now connect with Xero to show invoice statuses across jobs,
                    schedules, and dashboards.
                </p>
                <p>
                    Once connected, your team can see Xero invoice progress without switching
                    between apps.
                </p>
            </>
        ),
    },
    {
        title: 'Connect Xero',
        subtitle: 'Start by connecting this company to its Xero organisation.',
        icon: FiLink,
        content: (
            <>
                <img
                    className="w-full max-w-full rounded-xl object-cover"
                    src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/guides/xeroIntegrationSetup/connectButtonBanner.png"
                    alt=""
                />
                <p>
                    Go to the Xero Integration section and click <b>Connect Xero</b>.
                    A popup will open and ask you to approve access.
                </p>
                <p>
                    This will give Notify permission to see invoice updates on Xero.
                </p>
            </>
        ),
    },
    {
        title: 'Use the Reference Field',
        subtitle: 'This is the most important part of matching invoices correctly.',
        icon: FiCopy,
        content: (
            <>
                <img
                    className="w-full max-w-full rounded-xl object-cover"
                    src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/guides/xeroIntegrationSetup/xeroReferenceBanner.png"
                    alt=""
                />
                <p>
                    In Xero, every invoice should use the Notify department / child ID in the
                    invoice <b>Reference</b> field.
                </p>

                <div className="rounded-2xl bg-zinc-950 text-white px-4 py-3 font-mono text-sm">
                    N123R1
                </div>

                <p>
                    Notify uses this reference to match the Xero invoice with the correct quote
                    or department row.
                </p>
            </>
        ),
    },
    {
        title: 'Choose Display Modes',
        subtitle: 'Control how invoice statuses appear across Notify.',
        icon: FiSettings,
        content: (
            <>
                <img
                    className="w-full max-w-full rounded-xl object-cover"
                    src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/guides/xeroIntegrationSetup/chooseDisplayModes.png"
                    alt=""
                />
                <p>
                    <b>Display Source</b> controls where invoice statuses come from.
                </p>
                <p>
                    Choose <b>Notify Invoices</b>, <b>Xero Invoices</b>, or <b>Hybrid</b>.
                    Hybrid shows Xero status when available, otherwise it falls back to Notify.
                </p>
                <p>
                    <b>Status Labels</b> controls how Xero statuses are named. You can use
                    simplified labels like <b>Needs Approval</b> and <b>Invoiced</b>, or keep
                    original Xero labels like <b>Submitted</b> and <b>Authorised</b>.
                </p>
            </>
        ),
    },
    {
        title: 'View Invoice PDFs',
        subtitle: 'Open Xero invoice PDFs directly from Notify.',
        icon: FiFileText,
        content: (
            <>
                <img
                    className="w-full max-w-full rounded-xl object-cover"
                    src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/guides/xeroIntegrationSetup/viewXeroPDF%20(1).png"
                    alt=""
                />
                <p>
                    When a quote or department is linked to a Xero invoice, clicking its invoice
                    status field can open the Xero invoice PDF in a modal.
                </p>
                <p>
                    This makes it easy to review invoice details without leaving the job page.
                </p>
            </>
        ),
    },
    {
        title: 'Automatic Updates',
        subtitle: 'After setup, statuses update through Xero webhooks.',
        icon: FiRefreshCw,
        content: (
            <>
                <img
                    className="w-full max-w-full rounded-xl object-cover"
                    src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/guides/xeroIntegrationSetup/automaticUpdates.png"
                    alt=""
                />
                <p>
                    When an invoice changes in Xero, Notify receives the update, fetches the
                    invoice, matches it by reference, and updates the quote’s Xero invoice status.
                </p>
                <p>
                    Users do not need to manually sync from the settings page.
                </p>
            </>
        ),
    },
];

const IntegrationXeroSetup = ({ open, onClose }) => {
    const [step, setStep] = useState(0);

    if (!open) return null;

    const current = steps[step];
    const Icon = current.icon;
    const isFirst = step === 0;
    const isLast = step === steps.length - 1;

    return createPortal(
        <div
            className="mobile-fullscreen-dialog fixed inset-0 z-[2147483647] bg-white sm:bg-black/60 sm:backdrop-blur-sm"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2147483647,
            }}
        >
            <div className="h-[100dvh] w-full bg-white sm:max-w-3xl sm:h-[88dvh] sm:rounded-[2rem] sm:shadow-2xl sm:mx-auto sm:my-[6dvh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="shrink-0 flex items-center justify-between px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-100 bg-white">
                    <div className="min-w-0 pr-3">
                        <h2 className="text-lg sm:text-xl font-black text-zinc-800 truncate">
                            Xero Integration Setup
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            Step {step + 1} of {steps.length}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => { onClose(); setStep(0) }}
                        className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-zinc-700 shrink-0"
                    >
                        <FiX />
                    </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                            <Icon className="text-xl sm:text-2xl" />
                        </div>

                        <div className="w-full min-w-0">
                            <h3 className="text-xl sm:text-2xl font-black text-zinc-800">
                                {current.title}
                            </h3>

                            <p className="text-sm text-gray-500 mt-1">
                                {current.subtitle}
                            </p>

                            <div className="mt-5 space-y-4 text-sm text-zinc-600 leading-relaxed [&_img]:w-full [&_img]:max-w-full [&_img]:rounded-xl [&_img]:object-cover">
                                {current.content}
                            </div>
                        </div>
                    </div>


                </div>

                {/* Footer */}
                <div className="shrink-0 bg-white border-t border-gray-100 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+18px)] sm:px-6 sm:py-5 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
                        disabled={isFirst}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-zinc-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <FiArrowLeft />
                        Back
                    </button>

                    <div className="flex gap-2 self-center content-center justify-center items-center">
                        {steps.map((_, i) => (
                            <div
                                key={i}
                                className={`h-2 rounded-full transition-all ${i === step ? 'w-8 bg-zinc-900' : 'w-2 bg-gray-200'
                                    }`}
                            />
                        ))}
                    </div>

                    {isLast ? (
                        <button
                            type="button"
                            onClick={() => { onClose(); setStep(0) }}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black text-white bg-zinc-900 hover:bg-zinc-800"
                        >
                            Done
                            <FiCheckCircle />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setStep((prev) => Math.min(prev + 1, steps.length - 1))}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black text-white bg-zinc-900 hover:bg-zinc-800"
                        >
                            Next
                            <FiArrowRight />
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default IntegrationXeroSetup;